/**
 * 🎯 ExecutionSelectionContext — Cross-module selection tracking
 *
 * Tracks what's currently selected across the app:
 * - selectedInstanceId: workflow instance in focus
 * - selectedTaskId: task in focus (Minha Mesa, Gestão, Execution)
 * - selectedProblemId: problem in focus
 *
 * Any module can read/write. Used to arm ribbons and propagate
 * selection when navigating between modules (e.g. OpsCenter → MinhaMesa).
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface ExecutionSelection {
  selectedInstanceId: string | null;
  selectedTaskId: string | null;
  selectedProblemId: string | null;
}

interface ExecutionSelectionContextValue extends ExecutionSelection {
  selectInstance: (id: string | null) => void;
  selectTask: (id: string | null) => void;
  selectProblem: (id: string | null) => void;
  clearSelection: () => void;
}

const EMPTY: ExecutionSelection = {
  selectedInstanceId: null,
  selectedTaskId: null,
  selectedProblemId: null,
};

const ExecutionSelectionContext = createContext<ExecutionSelectionContextValue>({
  ...EMPTY,
  selectInstance: () => {},
  selectTask: () => {},
  selectProblem: () => {},
  clearSelection: () => {},
});

export const ExecutionSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selection, setSelection] = useState<ExecutionSelection>(EMPTY);

  const selectInstance = useCallback((id: string | null) => {
    setSelection(prev => ({ ...prev, selectedInstanceId: id }));
  }, []);

  const selectTask = useCallback((id: string | null) => {
    setSelection(prev => ({ ...prev, selectedTaskId: id }));
  }, []);

  const selectProblem = useCallback((id: string | null) => {
    setSelection(prev => ({ ...prev, selectedProblemId: id }));
  }, []);

  const clearSelection = useCallback(() => {
    setSelection(EMPTY);
  }, []);

  const value = useMemo<ExecutionSelectionContextValue>(() => ({
    ...selection,
    selectInstance,
    selectTask,
    selectProblem,
    clearSelection,
  }), [selection, selectInstance, selectTask, selectProblem, clearSelection]);

  return (
    <ExecutionSelectionContext.Provider value={value}>
      {children}
    </ExecutionSelectionContext.Provider>
  );
};

export function useExecutionSelection() {
  return useContext(ExecutionSelectionContext);
}
